import React, { useRef, useState } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalActions,
} from '@lightspeed/design-system-react'
import { ApiCustomer, ApiStoreCreditTransaction, ApiUser } from '@vend/types'
import { getDisplayNameFromApiCustomer } from '@vend/react-utilities'
import { useT } from '@transifex/react'
import VendNumber, { round } from 'vend-number'
import {
  IssueStoreCreditForm,
  IssueStoreCreditRef,
} from '../components/store-credit/IssueStoreCreditForm'
import { useUserManager } from '../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../state/hooks'
import {
  selectCurrentSale,
  storeCreditBalanceChanged,
} from '../state/slices/currentSale/currentSaleSlice'
import { wrToastNegative } from '../components/react/helios/ToastNotifications/WrToastNotifications'
import { $rootScope } from '../ng-compat/imports'
import { EVENT } from '../constants'
import { StoreCreditSummary } from '../components/store-credit/StoreCreditSummary'
import { customerBalancesManager } from '../managers/customer-balances-manager'

export type StoreCreditIssuedData = {
  transaction: ApiStoreCreditTransaction
  storeCredit: {
    balance: number
  }
  customer: ApiCustomer
  user: ApiUser
}

type Props = {
  onClose: () => void
  customer: ApiCustomer
}
export const StoreCreditModal = (props: Props) => {
  const { onClose, customer } = props
  const userManager = useUserManager()

  const [issueStoreCreditFormValid, setIssueStoreCreditFormValid] = useState(
    false
  )
  const [
    storeCreditIssuedData,
    setStoreCreditIssuedData,
  ] = useState<StoreCreditIssuedData>()
  const [isIssuingStoreCredit, setIsIssuingStoreCredit] = useState(false)
  const currentSale = useAppSelector(selectCurrentSale)
  const dispatch = useAppDispatch()
  const t = useT()
  const header = storeCreditIssuedData
    ? t('Store credit issued', { _context: 'Modal header' })
    : t('Issue store credit', { _context: 'Modal header' })

  const issueStoreCredit = async () => {
    const data = issueStoreCreditRef.current?.getFormData()
    if (!data) {
      return
    }
    const storeCreditData = {
      amount: Number(data?.amount),
      notes: data?.notes,
    }

    try {
      setIsIssuingStoreCredit(true)
      const transaction = await customerBalancesManager.issueStoreCredit(
        customer.id,
        storeCreditData
      )

      const storeCredit = await customerBalancesManager.getCustomerStoreCredit(
        customer
      )

      if (!storeCredit?.id) {
        return null
      }

      if (currentSale.customerBalances) {
        dispatch(storeCreditBalanceChanged(storeCredit.balance))
      }

      const storeCreditIssued = {
        transaction,
        customer,
        user: userManager.getLoggedInUser(),
        storeCredit,
      }

      setStoreCreditIssuedData(storeCreditIssued)

      $rootScope.$emit(EVENT.SALE_CUSTOMER_UPDATED)
    } catch {
      wrToastNegative('issue.store.credit.negative')
    } finally {
      setIsIssuingStoreCredit(false)
    }
  }

  const printReceipt = () => {
    if (!storeCreditIssuedData) {
      return
    }
    const { user, customer, transaction } = storeCreditIssuedData
    const { amount, created_at: date, type } = transaction

    const balance = currentSale.customer
      ? customerBalancesManager.calculateStoreCreditBalance()
      : amount
    $rootScope.$emit(EVENT.PRINT, {
      type: 'store-credit-issue-receipt',
      data: {
        amount,
        balance: round(balance, 2, VendNumber.ROUNDING_MODES.ROUND_DOWN),
        customerInfo: `${getDisplayNameFromApiCustomer(customer)} (${
          customer.customer_code
        })`,
        date,
        type,
        username: user.display_name,
      },
    })
  }
  const issueStoreCreditRef = useRef<IssueStoreCreditRef>(null)

  const formValidChanged = (valid: boolean) => {
    setIssueStoreCreditFormValid(valid)
  }
  return (
    <Modal size="medium" onClose={onClose}>
      <ModalHeader>{header}</ModalHeader>
      <ModalContent>
        <div className="vd-mb5">
          <span className="vd-header">
            {getDisplayNameFromApiCustomer(customer)}
          </span>
        </div>
        {!storeCreditIssuedData && (
          <IssueStoreCreditForm
            formValidChanged={formValidChanged}
            ref={issueStoreCreditRef}
          />
        )}
        {storeCreditIssuedData && (
          <StoreCreditSummary storeCreditIssuedData={storeCreditIssuedData} />
        )}
      </ModalContent>
      <ModalActions>
        {storeCreditIssuedData ? (
          <button className="vd-btn vd-btn--go" onClick={printReceipt}>
            {t('Print Receipt', { _context: 'Button label' })}
          </button>
        ) : (
          <button
            className="vd-btn vd-btn--go"
            onClick={issueStoreCredit}
            disabled={!issueStoreCreditFormValid || isIssuingStoreCredit}
          >
            {t('Give Store Credit', { _context: 'Button label' })}
          </button>
        )}
      </ModalActions>
    </Modal>
  )
}
